import * as vp from 'vopay';
import {
  Box,
  Pagination,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
  formattedCurrency,
  usePagination,
  useUpdateQueryParams,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import type { DefaultOptionProps } from 'vopay';
import { format, parseISO, subYears } from 'date-fns';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import ScheduledTransactionsForm from '@/components/ClientAccounts/ClientAccountSchedule/ScheduledTransactionsForm';
import useFetchAccountTransferSchedules from '@/hooks/useApi/useFetchAccountTransferSchedules';
import { useClientData } from '@/providers/ClientData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useI18nLabels } from '@/utils/useI18nLabels';
import ActivityType from '../ActivityType';
import type { ActivityTabType } from '../ActivityTab';
import { TableFiltersContainer, TransactionStatus, Table } from 'UI';
import styles from '../ActivityTab.module.scss';

type QueryParams = {
  status?: string;
  transferType?: string;
  frequency?: string;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  activityType?: ActivityTabType;
};

type TransactionParams = {
  status: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  frequency: string;
  transferType: string;
  hasInvalidEndDate: boolean;
  offset?: number;
};

type ScheduledClientAccountTransfersTableProps = {
  accountID: string;
  activityTypeOptions: DefaultOptionProps[];
  currentType: string;
  handleCurrentType: (val: ActivityTabType) => void;
};

const ScheduledClientAccountTransfersTable: FunctionComponent<ScheduledClientAccountTransfersTableProps> = ({
  accountID,
  activityTypeOptions,
  currentType,
  handleCurrentType,
}) => {
  const alertDialog = useAlertDialog();
  const { response } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.Schedule' });
  const accountIDType = response.clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [filteredList, setFilteredList] = useState<vp.AccountTransferSchedule[]>([]);
  const [scheduledTransactionsResponse, , , fetchScheduledTransactions] = useFetchAccountTransferSchedules({});
  const { frequencyLabelMapper, transferTypeLabelMapper } = useI18nLabels();

  const initialStartDate = format(subYears(new Date(), 1), 'yyyy-MM-dd');
  const initialEndDate = format(new Date(), 'yyyy-MM-dd');
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const transferType = useMemo(() => queryParams.get('transferType') || '', [queryParams]);
  const status = useMemo(() => queryParams.get('status') || '', [queryParams]);
  const frequency = useMemo(() => queryParams.get('frequency') || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);
  const startDate = useMemo(() => queryParams.get('startDate') || initialStartDate || '', [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || initialEndDate || '', [queryParams]);
  const filtersApplied = useMemo(() => {
    return Boolean(
      transferType ||
        status ||
        frequency ||
        searchTerm ||
        (startDate && startDate !== initialStartDate) ||
        (endDate && endDate !== initialEndDate),
    );
  }, [transferType, status, frequency, searchTerm, startDate, endDate, initialStartDate, initialEndDate]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  // pagination
  const totalDataLength = useMemo(() => {
    return scheduledTransactionsResponse.PaginationMetaData?.TotalRecords || 0;
  }, [scheduledTransactionsResponse.ScheduledClientAccountTransfers]);

  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const fetchScheduledTransactionHistory = async ({
    startDate,
    endDate,
    status,
    hasInvalidEndDate,
    searchTerm,
    frequency,
    transferType,
    offset = 0,
  }: TransactionParams) => {
    const scheduledTransactionHistoryRequest = {
      StartDate: startDate,
      EndDate: endDate,
      SearchText: searchTerm,
      ...(transferType === 'all' ? {} : { TransferType: transferType }),
      ...(frequency === 'all' ? {} : { TransferFrequency: frequency }),
      ...(status === 'all' ? {} : { TransferStatus: status }),
      Limit: PER_PAGE,
      Offset: offset,
      [accountIDType]: accountID,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }

    try {
      const res = await fetchScheduledTransactions(scheduledTransactionHistoryRequest);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const scheduledTransactionRequest = Object.values(
        (res.ScheduledClientAccountTransfers as { [key: number]: vp.AccountTransferSchedule }) || {},
      );
      setFilteredList(scheduledTransactionRequest);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('FetchScheduledTransactionsError.defaultMessage');
      alertDialog.error(t('FetchScheduledTransactionsError.title'), msg);
      console.error(err);
    } finally {
      setDataFetchingInfo(false);
    }
  };

  useEffect(() => {
    const offset = setPageIndex(0);
    fetchScheduledTransactionHistory({
      startDate,
      endDate,
      status,
      hasInvalidEndDate: false,
      searchTerm,
      frequency,
      transferType,
      offset,
    });
    updateQueryParams({
      activityType: 'Scheduled Client Account Transfers',
    });
  }, []);

  useEffect(() => {
    setHasInvalidEndDate(startDate > endDate);
    fetchScheduledTransactionHistory({
      status: status,
      searchTerm: searchTerm,
      startDate: format(new Date(startDate), 'yyyy-MM-dd'),
      endDate: format(new Date(endDate), 'yyyy-MM-dd'),
      transferType: transferType,
      frequency: frequency,
      hasInvalidEndDate: startDate > endDate,
    });
  }, [status, searchTerm, startDate, endDate, transferType, frequency]);

  const tableProps: vp.TableProps<vp.AccountTransferSchedule> = {
    id: 'scheduled-transactions-list',
    version: '2',
    columns: [
      {
        id: 'id',
        header: t('Table.id'),
        align: 'left',
        template: ({ data }) => (
          <Table.Link
            to={{
              pathname: `/client-account-schedule/details/${data.ID}`,
            }}
          >
            {data.ID}
          </Table.Link>
        ),
      },
      {
        id: 'debitorClientAccountID',
        header: t('Table.debitorClientAccountID'),
        align: 'left',
        field: 'DebitorClientAccountID',
      },
      {
        id: 'recipientClientAccountID',
        header: t('Table.recipientClientAccountID'),
        align: 'left',
        template: ({ data }) =>
          data.RecipientClientAccountID ??
          (JSON.parse(data.RecipientClientAccountIDSplit) as { ClientAccountID: string }[])
            .map((item) => item.ClientAccountID)
            .join(', '),
      },
      {
        id: 'transferType',
        header: t('Table.transferType'),
        align: 'left',
        template: ({ data }) => transferTypeLabelMapper(data.TransferType),
      },
      {
        id: 'frequency',
        header: t('Table.frequency'),
        align: 'left',
        template: ({ data }) => (data.Frequency !== '' ? frequencyLabelMapper(data.Frequency) : ''),
      },
      {
        id: 'amount',
        header: t('Table.amount'),
        align: 'left',
        template: ({ data }) => `$ ${formattedCurrency(data.Amount.toString())} ${data.Currency}`,
      },
      {
        id: 'startDate',
        header: t('Table.startDate'),
        align: 'left',
        field: 'ScheduleStartDate',
      },
      {
        id: 'ending',
        header: t('Table.ending'),
        align: 'left',
        template: ({ data }) =>
          data.EndingAfterPayments
            ? t('Table.Row.ending', { number: data.EndingAfterPayments })
            : data.ScheduleEndDate || '',
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus status={GetColorStatus(data.Status)} label={capitalizeFirstLetterOfWords(data.Status)} />
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item, index) => ({
      id: item.ID || String(index),
      data: item,
    })),
  };

  return (
    <Box flx="col">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="sb" ai="center">
          <ActivityType
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />
          <TableFiltersContainer.Trigger />
        </Box>
        <TableFiltersContainer.Content>
          <ScheduledTransactionsForm
            frequency={frequency}
            status={status}
            searchTerm={searchTerm}
            transferType={transferType}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
            initialTimeRange={[
              parseISO(startDate).getTime() as vp.Timestamp,
              parseISO(endDate).getTime() as vp.Timestamp,
            ]}
            onSearch={(searchParams) => {
              updateQueryParams({
                transferType: searchParams.transferType || '',
                frequency: searchParams.frequency || '',
                searchTerm: searchParams.searchTerm || '',
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                status: searchParams.status || '',
              });
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <div className={styles.tableContainer}>
        <Table {...tableProps} />
      </div>
      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);
              fetchScheduledTransactionHistory({
                startDate,
                endDate,
                status,
                hasInvalidEndDate: startDate > endDate,
                searchTerm,
                frequency,
                transferType,
                offset,
              });
            }}
          />
        )}
      </Box>

      {tableProps.rows && tableProps.rows.length === 0 && (
        <Box flx="col" jc="center">
          <Box ai="center" jc="center" pt="md">
            {t('Table.Messages.noRecordsToDisplay')}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ScheduledClientAccountTransfersTable;
